// src/components/dashboard/settings/MedicationManagement.js

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  TablePagination,
} from "@mui/material";

// Core Stable MUI Icons
import MedicationRoundedIcon from "@mui/icons-material/MedicationRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

const MedicationManagement = () => {
  // Live Dark/Light Mode Synchronizer
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  // Realistic Clinical Prescription Dataset
  const [rows, setRows] = useState([
    {
      id: 2,
      genericName: "Levetiracetam",
      brandName: "Keppra",
      dosageForm: "Tablet 500mg",
      status: "active",
    },
    {
      id: 3,
      genericName: "Gabapentin",
      brandName: "Neurontin",
      dosageForm: "Capsule 300mg",
      status: "active",
    },
    {
      id: 4,
      genericName: "Sumatriptan Succinate",
      brandName: "Imitrex",
      dosageForm: "Oral Tablet 50mg",
      status: "active",
    },
    {
      id: 5,
      genericName: "Pregabalin",
      brandName: "Lyrica",
      dosageForm: "Capsule 75mg",
      status: "active",
    },
    {
      id: 6,
      genericName: "Donepezil Hydrochloride",
      brandName: "Aricept",
      dosageForm: "Film-Coated Tablet 10mg",
      status: "active",
    },
    {
      id: 7,
      genericName: "Carbidopa / Levodopa",
      brandName: "Sinemet",
      dosageForm: "Oral Tablet 25/100mg",
      status: "active",
    },
    {
      id: 8,
      genericName: "Topiramate",
      brandName: "Topamax",
      dosageForm: "Tablet 25mg",
      status: "inactive",
    },
  ]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [openModal, setOpenModal] = useState(false);

  // New Medication Modal State
  const [newEntry, setNewEntry] = useState({
    genericName: "",
    brandName: "",
    dosageForm: "Tablet",
    status: "active",
  });

  // Unified Clinical Design System Palette
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const addGreen = "#057A44";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";

  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const tableHeaderBg = isDark ? "#162238" : "#F4F9F9";
  const rowHoverBg = isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.04)";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const dropdownBg = isDark ? "#1E293B" : "#FFFFFF";

  const handleDelete = (id) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleCreate = () => {
    if (!newEntry.genericName.trim() || !newEntry.brandName.trim()) return;
    const nextId = Math.max(...rows.map((r) => r.id), 0) + 1;
    setRows([{ id: nextId, ...newEntry }, ...rows]);
    setNewEntry({
      genericName: "",
      brandName: "",
      dosageForm: "Tablet",
      status: "active",
    });
    setOpenModal(false);
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        borderRadius: "8px",
        color: primaryTextColor,
        boxShadow: isDark ? "0 12px 32px rgba(0, 0, 0, 0.6)" : "0 8px 24px rgba(15, 23, 42, 0.08)",
        "& .MuiMenuItem-root": {
          fontSize: "0.82rem",
          py: 0.8,
          "&:hover": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : pastelTealBg,
          },
          "&.Mui-selected": {
            backgroundColor: isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg,
            color: tealButton,
            fontWeight: 700,
          },
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. TOP HEADER & ACTION BAR */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              backgroundColor: tealMain,
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <MedicationRoundedIcon sx={{ fontSize: "1.85rem" }} />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Typography
                sx={{
                  fontSize: { xs: "1.2rem", md: "1.35rem" },
                  fontWeight: 800,
                  color: primaryTextColor,
                  letterSpacing: "-0.3px",
                  lineHeight: 1.2,
                }}
              >
                Medication Management
              </Typography>
              <Chip
                label="Rx Formulary"
                size="small"
                sx={{
                  height: "22px",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  backgroundColor: pastelTealBg,
                  color: tealButton,
                  borderRadius: "6px",
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "0.82rem",
                color: secondaryTextColor,
                fontWeight: 500,
                mt: 0.3,
              }}
            >
              Manage prescription catalog, dosage formulations, and active clinical medications
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={() => setOpenModal(true)}
          startIcon={<AddRoundedIcon sx={{ fontSize: "1.15rem !important" }} />}
          sx={{
            height: "40px",
            backgroundColor: addGreen,
            color: "#FFFFFF",
            fontWeight: 700,
            fontSize: "0.82rem",
            borderRadius: "6px",
            px: 2.5,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#046337",
              boxShadow: "none",
            },
          }}
        >
          Add Medication
        </Button>
      </Box>

      {/* 2. MEDICATION DATA TABLE */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "16px",
          border: `1px solid ${softCardBorder}`,
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          overflow: "hidden",
        }}
      >
        <TableContainer
          sx={{
            maxHeight: "620px",
            overflowY: "auto",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Table stickyHeader sx={{ minWidth: 880 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: tealButton,
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                    backgroundColor: tableHeaderBg,
                    borderBottom: `1px solid ${softCardBorder}`,
                    py: 1.5,
                  },
                }}
              >
                <TableCell sx={{ width: "90px" }}>ID</TableCell>
                <TableCell>Generic Name</TableCell>
                <TableCell>Brand Name</TableCell>
                <TableCell>Dosage Form</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" sx={{ width: "90px", pr: 3 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 8, textAlign: "center" }}>
                    <Typography sx={{ color: secondaryTextColor, fontSize: "0.88rem", fontWeight: 600 }}>
                      No medications registered
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        transition: "background-color 0.15s ease",
                        "&:hover": { backgroundColor: rowHoverBg },
                        "& td": {
                          borderBottom: `1px solid ${
                            isDark ? "rgba(255, 255, 255, 0.04)" : "#F1F5F9"
                          }`,
                          py: 1.4,
                        },
                      }}
                    >
                      {/* ID Pill Badge */}
                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 1,
                            py: 0.25,
                            borderRadius: "6px",
                            backgroundColor: pastelTealBg,
                            color: tealButton,
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            fontFamily: "monospace",
                          }}
                        >
                          {row.id}
                        </Box>
                      </TableCell>

                      {/* Generic Name */}
                      <TableCell sx={{ fontWeight: 700, color: primaryTextColor, fontSize: "0.86rem" }}>
                        {row.genericName}
                      </TableCell>

                      {/* Brand Name */}
                      <TableCell sx={{ color: tealButton, fontSize: "0.82rem", fontWeight: 700 }}>
                        {row.brandName}
                      </TableCell>

                      {/* Dosage Form */}
                      <TableCell sx={{ color: primaryTextColor, fontSize: "0.82rem", fontWeight: 500 }}>
                        {row.dosageForm}
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          icon={
                            row.status === "active" ? (
                              <CheckCircleRoundedIcon sx={{ fontSize: "0.85rem !important", color: "#15803D !important" }} />
                            ) : undefined
                          }
                          label={row.status}
                          size="small"
                          sx={{
                            height: "22px",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            textTransform: "capitalize",
                            borderRadius: "6px",
                            backgroundColor:
                              row.status === "active"
                                ? isDark ? "rgba(16, 185, 129, 0.15)" : "#DCFCE7"
                                : isDark ? "rgba(255, 255, 255, 0.05)" : "#F1F5F9",
                            color: row.status === "active" ? (isDark ? "#34D399" : "#15803D") : secondaryTextColor,
                          }}
                        />
                      </TableCell>

                      {/* Action */}
                      <TableCell align="right" sx={{ pr: 2.5 }}>
                        <Tooltip title="Delete Medication">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row.id)}
                            sx={{
                              color: "#EF4444",
                              p: 0.6,
                              borderRadius: "6px",
                              "&:hover": {
                                backgroundColor: "rgba(239, 68, 68, 0.08)",
                              },
                            }}
                          >
                            <DeleteOutlineRoundedIcon sx={{ fontSize: "1.1rem" }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Bottom Pagination Bar */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: `1px solid ${softCardBorder}`,
            color: secondaryTextColor,
            "& .MuiTablePagination-select": {
              color: primaryTextColor,
              fontWeight: 600,
            },
            "& .MuiTablePagination-selectIcon": {
              color: secondaryTextColor,
            },
          }}
        />
      </Box>

      {/* 3. ADD MEDICATION MODAL DIALOG */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: cardBg,
            borderRadius: "16px",
            border: `1.5px solid ${cardBorder}`,
            color: primaryTextColor,
            p: 1.5,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: "1.15rem", color: primaryTextColor, pb: 1 }}>
          Add Medication
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
              Generic Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Levetiracetam"
              value={newEntry.genericName}
              onChange={(e) => setNewEntry({ ...newEntry, genericName: e.target.value })}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: controlBg,
                  color: primaryTextColor,
                  fontSize: "0.85rem",
                  "& fieldset": { borderColor: controlBorder },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
              }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
              Brand Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. Keppra"
              value={newEntry.brandName}
              onChange={(e) => setNewEntry({ ...newEntry, brandName: e.target.value })}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: controlBg,
                  color: primaryTextColor,
                  fontSize: "0.85rem",
                  "& fieldset": { borderColor: controlBorder },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                },
              }}
            />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
                Dosage Form
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Tablet 500mg"
                value={newEntry.dosageForm}
                onChange={(e) => setNewEntry({ ...newEntry, dosageForm: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    color: primaryTextColor,
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: controlBorder },
                    "&:hover fieldset": { borderColor: tealMain },
                    "&.Mui-focused fieldset": { borderColor: tealMain },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, mb: 0.6, color: labelTeal }}>
                Status
              </Typography>
              <Select
                fullWidth
                size="small"
                value={newEntry.status}
                onChange={(e) => setNewEntry({ ...newEntry, status: e.target.value })}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuProps}
                sx={{
                  borderRadius: "6px",
                  backgroundColor: controlBg,
                  color: primaryTextColor,
                  fontSize: "0.85rem",
                  "& fieldset": { borderColor: controlBorder },
                  "&:hover fieldset": { borderColor: tealMain },
                  "&.Mui-focused fieldset": { borderColor: tealMain },
                  "& .MuiSelect-icon": { color: secondaryTextColor },
                }}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => setOpenModal(false)}
            sx={{
              color: secondaryTextColor,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            sx={{
              backgroundColor: addGreen,
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
              px: 2.8,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#046337",
                boxShadow: "none",
              },
            }}
          >
            Save Medication
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicationManagement;